import { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { ChevronLeft, Download, FileText, CheckCircle } from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";
const rebelLogo = "/assets/d85ba7a4098672812ab5e992f2ac839653942275.png";
import { LanguageSwitcher } from "./LanguageSwitcher";

type Props = {
  onNavigateBack: () => void;
  onNavigateToThankYou: () => void;
};

export function WhitepaperPage({ onNavigateBack, onNavigateToThankYou }: Props) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    interests: [] as string[],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const interestOptions = t("whitepaper.form.interests", { returnObjects: true }) as string[];

  const handleInterestChange = (interest: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      interests: checked
        ? [...prev.interests, interest]
        : prev.interests.filter((i) => i !== interest),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      const apiBase = import.meta.env.VITE_API_BASE || 'http://localhost:8787';
      const payload = {
        name: formData.name,
        email: formData.email,
        company: formData.company || null,
        phone: formData.phone,
        interests: formData.interests,
      };

      const res = await fetch(`${apiBase}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error('Error al enviar el formulario');
      }

      setSubmitSuccess(true);
      setTimeout(() => {
        onNavigateToThankYou();
      }, 2000);
    } catch (error) {
      console.error('Error:', error);
      alert(t("whitepaper.errors.submitError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    const formElement = document.getElementById('whitepaper-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (submitSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center text-white p-8"
        >
          <CheckCircle className="w-16 h-16 mx-auto mb-4" />
          <h2 className="text-3xl mb-4">{t("whitepaper.success.title")}</h2>
          <p className="text-xl">{t("whitepaper.success.message")}</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-sm border-b border-orange-500/30">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={onNavigateBack}
                className="text-black hover:text-orange-500 mr-4"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
{t("whitepaper.back")}
              </Button>
              <img
                src={rebelLogo}
                alt="Rebel Genomics"
                className="w-8 h-8 mr-2"
              />
              <span className="text-black text-lg">{t("whitepaper.brand")}</span>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSwitcher light />
              <Button
                size="lg"
                className="bg-orange-500 hover:bg-orange-600 text-white"
                onClick={scrollToForm}
              >
{t("cta.download")}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-20 pb-16 bg-gradient-to-br from-orange-50 to-orange-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-6xl font-bold text-gray-900 mb-6"
            >
              {t("whitepaper.hero.title")}
              <br />
              <span className="text-orange-500">{t("whitepaper.hero.titleHighlight")}</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl md:text-2xl text-gray-700 mb-8 leading-relaxed"
            >
              {t("whitepaper.hero.description")}
              <br />
              {t("whitepaper.hero.description2")}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <Button
                size="lg"
                className="bg-orange-500 hover:bg-orange-600 text-white text-xl px-12 py-6 rounded-full"
                onClick={scrollToForm}
              >
{t("cta.download")} {t("whitepaper.hero.downloadFree")}
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What You'll Learn Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t("whitepaper.whatYouLearn.title")}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t("whitepaper.whatYouLearn.description")}
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {(t("whitepaper.whatYouLearn.items", { returnObjects: true }) as Array<{title: string, description: string}>).map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-gray-50 p-6 rounded-2xl border border-orange-200"
                >
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {item.title}
                  </h3>
                  <p className="text-gray-600">
                    {item.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section id="whitepaper-form" className="py-16 bg-gradient-to-br from-orange-500 to-red-600">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="text-white"
              >
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  {t("whitepaper.download.title")}
                </h2>
                <p className="text-xl mb-8 leading-relaxed">
                  {t("whitepaper.download.description")}
                  <br />
                  <span className="font-semibold">{t("whitepaper.download.description2")}</span>
                </p>
                
                <div className="space-y-4">
                  {(t("whitepaper.download.benefits", { returnObjects: true }) as string[]).map((benefit, index) => (
                    <div key={index} className="flex items-center">
                      <CheckCircle className="w-6 h-6 text-green-300 mr-3" />
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20"
              >
                <div className="relative">
                  <ImageWithFallback
                    src="/assets/laboratorio vegetal.png"
                    alt="Book Mockup Guide PDF 3D Brochure"
                    className="w-full h-80 object-cover rounded-2xl"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-2xl flex items-end justify-center p-6">
                    <div className="text-center">
                      <Download className="w-8 h-8 text-white mx-auto mb-2" />
                      <h3 className="text-xl text-white">{t("whitepaper.download.pdfTitle")}</h3>
                    </div>
                  </div>
                </div>
                <div className="mt-6 text-center">
                  <h3 className="text-2xl mb-2 text-white-500">{t("whitepaper.download.guideTitle")}</h3>
                  <p className="text-gray-300 mb-4">{t("whitepaper.download.guideDescription")}</p>
                  <div className="flex items-center justify-center mt-4 space-x-4">
                    <div className="flex items-center">
                      <FileText className="w-4 h-4 text-white-500 mr-1" />
                      <span className="text-sm text-white-600">{t("whitepaper.download.format")}</span>
                    </div>
                    <div className="flex items-center">
                      <Download className="w-4 h-4 text-white-500 mr-1" />
                      <span className="text-sm text-white-600">{t("whitepaper.download.immediate")}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t("whitepaper.form.title")}
              </h2>
              <p className="text-xl text-gray-600">
                {t("whitepaper.form.description")}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-gray-50 p-8 rounded-3xl border border-orange-200"
            >
              <form onSubmit={handleSubmit} noValidate className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-gray-700 font-medium">
                      {t("whitepaper.form.labels.name")}
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      required
                      className="mt-2"
                      placeholder={t("whitepaper.form.placeholders.name")}
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-gray-700 font-medium">
                      {t("whitepaper.form.labels.email")}
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      required
                      className="mt-2"
                      placeholder={t("whitepaper.form.placeholders.email")}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="company" className="text-gray-700 font-medium">
                      {t("whitepaper.form.labels.company")}
                    </Label>
                    <Input
                      id="company"
                      type="text"
                      value={formData.company}
                      onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                      className="mt-2"
                      placeholder={t("whitepaper.form.placeholders.company")}
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-gray-700 font-medium">
                      {t("whitepaper.form.labels.phone")}
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      required
                      className="mt-2"
                      placeholder={t("whitepaper.form.placeholders.phone")}
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-gray-700 font-medium mb-4 block">
                    {t("whitepaper.form.labels.interests")}
                  </Label>
                  <div className="grid md:grid-cols-2 gap-3">
                    {interestOptions.map((interest) => (
                      <div key={interest} className="flex items-center space-x-3">
                        <Checkbox
                          id={interest}
                          checked={formData.interests.includes(interest)}
                          onCheckedChange={(checked) => handleInterestChange(interest, checked === true)}
                        />
                        <Label htmlFor={interest} className="text-gray-700 cursor-pointer">
                          {interest}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pt-4">
                  <Button
                    type="submit"
                    size="lg"
                    disabled={isSubmitting || formData.interests.length === 0}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white text-xl py-6 rounded-full"
                  >
                    {isSubmitting ? t("whitepaper.form.submitting") : t("cta.download")}
                  </Button>
                </div>

                <p className="text-sm text-gray-500 text-center">
                  {t("whitepaper.form.privacy")}
                  <br />
                  {t("whitepaper.form.privacy2")}
                </p>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <img
              src={rebelLogo}
              alt="Rebel Genomics"
              className="w-12 h-12 mx-auto mb-4"
            />
            <h3 className="text-2xl font-bold mb-4">{t("whitepaper.brand")}</h3>
            <p className="text-gray-400 mb-6">
              {t("whitepaper.footer.description")}
              <br />
              {t("whitepaper.footer.description2")}
            </p>
            <div className="flex justify-center space-x-6">
              <a href="#" className="text-gray-400 hover:text-orange-500 transition-colors">
                {t("whitepaper.footer.links.privacy")}
              </a>
              <a href="#" className="text-gray-400 hover:text-orange-500 transition-colors">
                {t("whitepaper.footer.links.terms")}
              </a>
              <a href="#" className="text-gray-400 hover:text-orange-500 transition-colors">
                {t("whitepaper.footer.links.contact")}
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}